import { Controller } from "@hotwired/stimulus";
import { submitFormWithTurbo, prepareFormData } from "./form_submission";
import { validateFile, validateHeaders, showMappingValidationErrors, showMappingValidationError, highlightUnmappedRequired, clearValidationErrors } from "./validation";
import { parseCSV, parseRow, processCSVFile } from "./csv_parser";
import { createPreviewTable, getMappedColumns } from "./table_preview";

export default class extends Controller {
  static targets = ["fileInput"]
  static values = {
    requiredHeaders: { type: Array, default: [] },
    optionalHeaders: { type: Array, default: [] },
    options: { type: Array, default: [] },
  }

  // Configuration constants
  static CONFIG = {
    MAX_ROWS: 5,
    CHUNK_SIZE: 1024 * 10, // 10KB
    MAX_FILE_SIZE: 50 * 1024 * 1024, // 25MB
  };

  connect() {
    this.initializeState();
  }

  initializeState() {
    this.config = { ...this.constructor.CONFIG };
    // Load headers from data attributes
    this.config.REQUIRED_HEADERS = this.requiredHeadersValue || [];
    this.config.OPTIONAL_HEADERS = this.optionalHeadersValue || [];
    this.config.OPTIONS = this.optionsValue || [];
    this.allHeaders = [...this.config.REQUIRED_HEADERS, ...this.config.OPTIONAL_HEADERS];
    this.mappedHeaders = {}; // Maps destination header to imported header
    this.rows = [];
    this.importedHeaders = []; // Store imported headers
    this.selectedFile = null;
  }

  previewFile(event) {
    const file = event.target.files[0];

    if (!this.validateFile(file)) return;

    this.selectedFile = file;
    this.showView('preview-view');
    this.showLoadingState();
    this.processFile(file);
  }

  validateFile(file) {
    const clearFileInput = () => {
      if (this.hasFileInputTarget) {
        this.fileInputTarget.value = '';
      }
    };

    return validateFile(file, this.config.MAX_FILE_SIZE, this.showError.bind(this), clearFileInput);
  }

  processFile(file) {
    processCSVFile(
      file,
      this.config.MAX_FILE_SIZE,
      this.config.CHUNK_SIZE,
      (text, fileSize) => this.handleFileLoad({ target: { result: text } }, fileSize),
      (error) => this.showError('Error reading file')
    );
  }

  handleFileLoad(event, fileSize) {
    try {
      const text = event.target.result;
      this.rows = parseCSV(text, fileSize, this.config.MAX_FILE_SIZE, this.config.MAX_ROWS, this.config.CHUNK_SIZE);

      if (this.rows.length === 0) {
        this.showError('No data to display');
        return;
      }

      this.importedHeaders = this.rows[0];
      this.renderMappingView();
    } catch (error) {
      this.showError('Error processing CSV file');
    }
  }

  validateHeaders(headers) {
    return validateHeaders(headers, this.config.REQUIRED_HEADERS);
  }

  renderMappingView(preserveMappings = false) {
    const container = this.createMappingContainer();
    const { mappingCol, previewCol } = this.createMappingColumns();

    this.addMappingTitle(mappingCol);
    this.addValidationErrorContainer(mappingCol);
    this.addMappingHeader(mappingCol);

    // Render mapping rows in the order defined in configuration
    const selects = this.renderMappingRowsInOrder(mappingCol, previewCol, preserveMappings);

    container.appendChild(this.createMainRow(mappingCol, previewCol));
    container.appendChild(this.createButtonContainer());

    this.getPreviewElement().innerHTML = '';
    this.getPreviewElement().appendChild(container);
  }

  createMappingContainer() {
    const container = document.createElement('div');
    container.className = 'container py-md-5 py-3';
    return container;
  }

  createMappingColumns() {
    const mappingCol = document.createElement('div');
    mappingCol.className = 'col-md-7';

    const previewCol = document.createElement('div');
    previewCol.className = 'col-md-5 mt-4 mt-md-0';
    previewCol.appendChild(this.createDataPreviewBox());

    return { mappingCol, previewCol };
  }

  createDataPreviewBox() {
    const dataPreview = document.createElement('div');
    dataPreview.className = 'preview-box';
    Object.assign(dataPreview.style, {
      borderRadius: '4px',
      padding: '20px',
      backgroundColor: '#fafbfd'
    });
    return dataPreview;
  }

  addMappingTitle(mappingCol) {
    const title = document.createElement('h4');
    title.className = 'mb-md-4 mb-3 font-weight-semibold';
    title.textContent = 'Review and Confirm Column Mappings';
    mappingCol.appendChild(title);
  }

  addValidationErrorContainer(mappingCol) {
    const validationContainer = document.createElement('div');
    validationContainer.className = 'validation-errors w-100 mb-4';
    validationContainer.style.display = 'none';
    mappingCol.appendChild(validationContainer);
  }

  addMappingHeader(mappingCol) {
    const headerRow = document.createElement('div');
    headerRow.className = 'row font-weight-medium text-muted mb-4';
    headerRow.innerHTML = `
      <div class="col-5">DESTINATION COLUMN</div>
      <div class="col-1"></div>
      <div class="col-6">IMPORTED COLUMN</div>
    `;
    mappingCol.appendChild(headerRow);
  }

  // Render mapping rows in the order defined in configuration (required first, then optional)
  renderMappingRowsInOrder(mappingCol, previewCol, preserveMappings = false) {
    if (!preserveMappings) {
      this.resetMappingState();
    }
    const selects = [];

    // Render required headers first in their defined order
    this.config.REQUIRED_HEADERS.forEach((header) => {
      const row = this.createMappingRow(header, true, selects, previewCol);
      mappingCol.appendChild(row);
    });

    // Then render optional headers in their defined order
    this.config.OPTIONAL_HEADERS.forEach((header) => {
      const row = this.createMappingRow(header, false, selects, previewCol);
      mappingCol.appendChild(row);
    });

    // Reapply existing mappings if preserving
    if (preserveMappings) {
      selects.forEach(select => {
        const destHeader = select.dataset.destHeader;
        const mappedValue = this.mappedHeaders[destHeader];
        if (mappedValue) {
          select.value = mappedValue;
          this.toggleClearButton(select, mappedValue);
          this.updateDataPreview(mappedValue, destHeader, previewCol);
        }
      });
    }

    return selects;
  }

  createMappingRow(destHeader, isRequired, selects, previewCol) {
    const row = document.createElement('div');
    row.className = 'mapping-row row align-items-center mb-2';

    row.appendChild(this.createDestinationColumnElement(destHeader, isRequired));
    row.appendChild(this.createArrowElement());
    row.appendChild(this.createImportedColumnElement(destHeader, selects, previewCol));

    return row;
  }

  createDestinationColumnElement(destHeader, isRequired) {
    const col = document.createElement('div');
    col.className = 'col-5';

    const tag = document.createElement('span');
    tag.className = 'tag px-2 py-1 d-inline-block';
    Object.assign(tag.style, {
      backgroundColor: isRequired ? '#ffeaa7' : '#e3e9ff',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '500'
    });
    tag.textContent = destHeader + (isRequired ? ' *' : '');

    col.appendChild(tag);
    return col;
  }

  createArrowElement() {
    const arrow = document.createElement('div');
    arrow.className = 'col-1 text-center arrow';
    arrow.style.fontSize = '1.2rem';
    arrow.textContent = '←';
    return arrow;
  }

  createImportedColumnElement(destHeader, selects, previewCol) {
    const col = document.createElement('div');
    col.className = 'col-6 position-relative';

    const select = this.createSelect(destHeader, selects, previewCol);
    const clearButton = this.createClearButton(destHeader, select, selects, previewCol);

    col.appendChild(select);
    col.appendChild(clearButton);
    selects.push(select);

    return col;
  }

  createSelect(destHeader, selects, previewCol) {
    const select = document.createElement('select');
    select.className = 'form-control';
    select.style.maxWidth = '250px';
    select.dataset.destHeader = destHeader;

    this.populateSelectOptions(select);
    this.addSelectChangeHandler(select, destHeader, selects, previewCol);

    return select;
  }

  populateSelectOptions(select) {
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Select a column';
    select.appendChild(defaultOption);

    this.importedHeaders.forEach(importedHeader => {
      const option = document.createElement('option');
      option.value = importedHeader;
      option.textContent = importedHeader || 'Empty';
      select.appendChild(option);
    });
  }

  addSelectChangeHandler(select, destHeader, selects, previewCol) {
    select.addEventListener('change', (e) => {
      this.handleSelectChange(e, destHeader, selects, previewCol);
    });
  }

  handleSelectChange(event, destHeader, selects, previewCol) {
    const newValue = event.target.value;
    const oldValue = this.mappedHeaders[destHeader];

    // Update mapping state - allow multiple destinations to map to same source
    this.mappedHeaders[destHeader] = newValue;
    this.updateDataPreview(newValue, destHeader, previewCol);
    this.toggleClearButton(event.target, newValue);

    this.clearValidationErrors();
  }

  createClearButton(destHeader, select, selects, previewCol) {
    const clearButton = document.createElement('button');
    clearButton.className = 'position-absolute btn btn-link text-muted p-0';
    Object.assign(clearButton.style, {
      right: '30px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'none'
    });
    clearButton.innerHTML = '✕';

    clearButton.addEventListener('click', () => {
      this.handleClearClick(destHeader, select, selects, previewCol);
    });

    return clearButton;
  }

  handleClearClick(destHeader, select, selects, previewCol) {
    select.value = '';
    this.mappedHeaders[destHeader] = '';
    this.clearDataPreview(previewCol);
    this.toggleClearButton(select, '');
  }

  toggleClearButton(select, value) {
    const clearButton = select.parentElement.querySelector('button');
    clearButton.style.display = value ? 'block' : 'none';
  }

  updateDataPreview(importedHeader, destHeader, previewCol) {
    const dataPreview = previewCol.querySelector('.preview-box');

    if (importedHeader && this.rows) {
      this.renderDataPreview(dataPreview, importedHeader, destHeader);
    } else {
      this.clearDataPreview(previewCol);
    }
  }

  renderDataPreview(dataPreview, importedHeader, destHeader) {
    dataPreview.innerHTML = '';

    const title = this.createPreviewTitle(importedHeader, destHeader);
    const list = this.createPreviewList(importedHeader);

    dataPreview.appendChild(title);
    dataPreview.appendChild(list);
  }

  createPreviewTitle(importedHeader, destHeader) {
    const title = document.createElement('p');
    title.className = 'font-weight-bold mb-3 mt-3';
    title.innerHTML = 'Data preview for ';

    const tag = document.createElement('span');
    tag.className = 'tag d-inline-block mt-2';
    Object.assign(tag.style, {
      backgroundColor: '#e3e9ff',
      padding: '4px 10px',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '500'
    });
    tag.textContent = importedHeader;

    title.appendChild(tag);

    const arrow = document.createElement('span');
    arrow.textContent = ' → ';
    arrow.style.margin = '0 8px';
    title.appendChild(arrow);

    const destTag = document.createElement('span');
    destTag.className = 'tag d-inline-block mt-2';
    Object.assign(destTag.style, {
      backgroundColor: '#ffeaa7',
      padding: '4px 10px',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '500'
    });
    destTag.textContent = destHeader;
    title.appendChild(destTag);

    return title;
  }

  createPreviewList(importedHeader) {
    const list = document.createElement('ul');
    list.className = 'list-group py-1';

    const columnIndex = this.importedHeaders.indexOf(importedHeader);
    this.rows.slice(1, 6).forEach(row => {
      const listItem = document.createElement('li');
      listItem.className = 'list-group-item py-1';
      listItem.textContent = row[columnIndex] || 'N/A';
      list.appendChild(listItem);
    });

    return list;
  }

  clearDataPreview(previewCol) {
    const dataPreview = previewCol.querySelector('.preview-box');
    dataPreview.innerHTML = '';
  }

  createMainRow(mappingCol, previewCol) {
    const mainRow = document.createElement('div');
    mainRow.className = 'row';
    mainRow.appendChild(mappingCol);
    mainRow.appendChild(previewCol);
    return mainRow;
  }

  createButtonContainer() {
    const container = document.createElement('div');
    container.className = 'd-flex justify-content-between align-items-center mt-3';

    container.appendChild(this.createBackButton());
    container.appendChild(this.createConfirmButton());

    return container;
  }

  createBackButton() {
    const button = document.createElement('button');
    button.className = 'btn btn-light cursor-pointer';
    button.textContent = 'Back to File Selection';
    button.addEventListener('click', () => this.handleBackClick());
    return button;
  }

  createConfirmButton() {
    const wrapper = document.createElement('div');
    wrapper.className = 'text-right';

    const button = document.createElement('button');
    button.className = 'btn btn-primary cursor-pointer';
    button.textContent = 'Confirm';
    button.addEventListener('click', () => this.handleConfirmClick());

    wrapper.appendChild(button);
    return wrapper;
  }

  createSubmitButton() {
    const wrapper = document.createElement('div');
    wrapper.className = 'text-right';

    const button = document.createElement('button');
    button.className = 'btn btn-primary mt-2 mb-md-0 mb-2 mr-md-0 mr-2';
    button.textContent = 'Submit';
    button.addEventListener('click', () => this.submitToServer());

    wrapper.appendChild(button);
    return wrapper;
  }

  createBackToMappingButton() {
    const wrapper = document.createElement('div');
    wrapper.className = 'text-right';

    const button = document.createElement('button');
    button.className = 'btn btn-light mt-2 mb-md-0 mb-2 mr-md-0 mr-2';
    button.textContent = 'Back to Mapping';
    button.addEventListener('click', () => this.renderMappingView(true));

    wrapper.appendChild(button);
    return wrapper;
  }

  handleBackClick() {
    this.showView('browse-file');
    window.location.reload();
    this.resetMappingState();
    this.getPreviewElement().innerHTML = '';
    if (this.fileInputTarget) {
      this.fileInputTarget.value = '';
    }
  }

  handleConfirmClick() {
    this.clearValidationErrors();

    const unmappedRequired = this.config.REQUIRED_HEADERS.filter(
      header => !this.mappedHeaders[header]
    );

    if (unmappedRequired.length > 0) {
      this.showMappingValidationErrors(unmappedRequired);
      return;
    }

    this.renderPreview(this.rows);
  }

  renderPreview(rows) {
    if (!rows?.length) {
      this.showError('No data to display');
      return;
    }

    const container = this.createMappingContainer();
    const previewHeader = document.createElement('h4');
    previewHeader.className = 'mb-md-4 mb-3 font-weight-semibold';
    previewHeader.textContent = 'Final Data Preview';
    container.appendChild(previewHeader);

    // Create a preview table that maintains the destination column order
    const table = this.createOrderedPreviewTable(rows);
    container.appendChild(table);

    this.config.OPTIONS.forEach((option) => {
      const checkbox = this.createCheckbox(option, 'generic_file_import[columns][create_product_if_not_found]');
      container.appendChild(checkbox);
    });

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'd-flex justify-content-between align-items-center mt-3';
    buttonContainer.appendChild(this.createBackToMappingButton());
    buttonContainer.appendChild(this.createSubmitButton());
    container.appendChild(buttonContainer);

    const previewElement = this.getPreviewElement();
    previewElement.innerHTML = '';
    previewElement.appendChild(container);
  }

  createPreviewTable(rows) {
    return createPreviewTable(rows, this.getMappedColumns.bind(this));
  }

  createOrderedPreviewTable(rows) {
    if (!rows || rows.length === 0) {
      return document.createElement('div');
    }

    const wrapperDiv = document.createElement('div');
    wrapperDiv.className = 'table-responsive';
    const table = document.createElement('table');
    table.className = 'table table-bordered border';

    // Get mapped columns in the order of destination headers (required first, then optional)
    const mappedColumns = this.getMappedColumnsInOrder();

    // Create header row with ordered columns
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');

    mappedColumns.forEach(({ destinationHeader }) => {
      const th = document.createElement('th');
      th.className = 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
      th.textContent = destinationHeader;
      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Create table body with ordered columns
    const tbody = document.createElement('tbody');
    tbody.className = 'bg-white divide-y divide-gray-200';

    const dataRows = rows.slice(1);
    dataRows.forEach(row => {
      const tr = document.createElement('tr');

      mappedColumns.forEach(({ sourceIndex }) => {
        const td = document.createElement('td');
        td.className = 'px-6 py-4 whitespace-nowrap text-sm text-gray-500';
        td.textContent = row[sourceIndex] ?? '';
        tr.appendChild(td);
      });

      tbody.appendChild(tr);
    });

    table.appendChild(tbody);
    wrapperDiv.appendChild(table);

    return wrapperDiv;
  }

  // Get mapped columns in the order of destination headers (not CSV order)
  getMappedColumnsInOrder() {
    const mappedColumns = [];

    // Process required headers first in their defined order
    this.config.REQUIRED_HEADERS.forEach(destHeader => {
      const importedHeader = this.mappedHeaders[destHeader];
      if (importedHeader) {
        const sourceIndex = this.importedHeaders.indexOf(importedHeader);
        if (sourceIndex !== -1) {
          mappedColumns.push({
            sourceIndex: sourceIndex,
            destinationHeader: destHeader,
            sourceHeader: importedHeader
          });
        }
      }
    });

    // Then process optional headers in their defined order
    this.config.OPTIONAL_HEADERS.forEach(destHeader => {
      const importedHeader = this.mappedHeaders[destHeader];
      if (importedHeader) {
        const sourceIndex = this.importedHeaders.indexOf(importedHeader);
        if (sourceIndex !== -1) {
          mappedColumns.push({
            sourceIndex: sourceIndex,
            destinationHeader: destHeader,
            sourceHeader: importedHeader
          });
        }
      }
    });

    return mappedColumns;
  }

  getMappedColumns(firstRow) {
    // Maintain compatibility with existing code that expects this method
    return this.getMappedColumnsInOrder();
  }

  submitToServer() {
    if (!this.selectedFile || !this.rows?.length) {
      this.showError('No file or data to submit');
      return;
    }

    const formData = prepareFormData(this.selectedFile, this.rows, this.getMappedColumns.bind(this), this.config.OPTIONS);
    const formPath = document.getElementById('form-path').getAttribute('attr-url');
    submitFormWithTurbo(formData, formPath, this.showLoadingState.bind(this));
  }

  showLoadingState(message = 'Loading...') {
    const previewElement = this.getPreviewElement();
    previewElement.innerHTML = `
      <div class="d-flex justify-content-center align-items-center" style="height: 200px;">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">${message}</span>
        </div>
        <span class="ml-2">${message}</span>
      </div>
    `;
  }

  showSuccess(message) {
    const previewElement = this.getPreviewElement();
    const successDiv = document.createElement('div');
    successDiv.className = 'alert alert-success mt-3';
    successDiv.textContent = message;
    previewElement.prepend(successDiv);
  }

  resetMappingState() {
    this.mappedHeaders = {};
  }

  showView(viewId) {
    const views = ['browse-file', 'preview-view', 'manual-input'];
    views.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        element.classList.toggle('d-none', id !== viewId);
      }
    });
  }

  showError(message) {
    const errorElement = document.getElementById('required-columns-info');
    const alertElement = errorElement?.closest('.alert');

    if (errorElement) {
      // Update the alert styling for error state
      if (alertElement) {
        alertElement.classList.remove('alert-primary');
        alertElement.classList.add('alert-danger');
      }

      // Set the error message
      errorElement.textContent = message;
      errorElement.style.color = '#721c24';
      errorElement.style.fontWeight = '500';

      // Make sure it's visible
      errorElement.style.display = 'block';

      // Scroll to the error message
      errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Fallback to the original behavior if the element is not found
      const previewElement = this.getPreviewElement();
      if (previewElement) {
        previewElement.innerHTML = `<div class="alert alert-danger">${message}</div>`;
      } else {
      }
    }
  }

  getPreviewElement() {
    return document.getElementById('preview-view');
  }

  showManually() {
    this.showView('manual-input');
  }

  showBrowseFile() {
    this.showView('browse-file');
  }

  showMappingValidationErrors(unmappedRequired) {
    showMappingValidationErrors(unmappedRequired, this.highlightUnmappedRequired.bind(this));
  }

  showMappingValidationError(message) {
    showMappingValidationError(message);
  }

  highlightUnmappedRequired(unmappedRequired) {
    highlightUnmappedRequired(unmappedRequired, this.mappedHeaders, this.config.REQUIRED_HEADERS);
  }

  clearValidationErrors() {
    clearValidationErrors();
  }

  createCheckbox(option) {
    const checkboxContainer = document.createElement('div');
    checkboxContainer.className = 'custom-control custom-checkbox';

    const checkboxInput = document.createElement('input');
    checkboxInput.className = 'custom-control-input boolean optional';
    checkboxInput.type = 'checkbox';
    checkboxInput.value = '1';
    checkboxInput.name = `generic_file_import[columns][${option.id}]`;
    checkboxInput.id = option.id;

    const checkboxLabel = document.createElement('label');
    checkboxLabel.className = 'custom-control-label boolean optional';
    checkboxLabel.htmlFor = option.id;
    checkboxLabel.textContent = option.text;

    checkboxContainer.appendChild(checkboxInput);
    checkboxContainer.appendChild(checkboxLabel);

    return checkboxContainer;
  }
}
